<template lang="pug">
  .features.row
    .row.feature
      simple-svg.feature-icon.dark-gray-icon(:filepath='bookIcon' width='20px')
      .column.m-left-1
        span.feature-title.m4-bottom-2
          | {{ innerI18n.t('listPrograms.resourcesLabel') }}
        .feature-value.text-small.m-right-1(v-if='content.educationalProgramResources.length')
          | {{ content.educationalProgramResources.length }}
        .feature-value.text-small.dark-gray.m-right-1(v-else)
          | {{ innerI18n.t('krea.educationalContent.features.resourcesAmountEmpty') }}
    .row.feature
      simple-svg.feature-icon.dark-gray-icon(:filepath='calendarIcon' width='20px')
      .column.m-left-1
        span.feature-title.m4-bottom-2
          | {{ innerI18n.t('listPrograms.durationLabel') }}
        .feature-value.text-small.m-right-1(v-if='content.classTotalQuantity')
          | {{  $tc('listPrograms.lectures', content.classTotalQuantity ) }}
        .feature-value.text-small.dark-gray.m-right-1(v-else)
          | {{ innerI18n.t('krea.educationalContent.features.durationAmountEmpty') }}
    .row.item-1#ageRanges
      v-popover.width-100(
        placement='top-start'
        :delay='{show: 100, hide: 600}'
        trigger='hover click'
        popoverClass='krea-actions-popover'
        :container='false'
      )
        .row.item-1
          simple-svg.feature-icon.dark-gray-icon(:filepath='userIcon' width='20px')
          .column.m-left-1.width-100
            span.feature-title.m4-bottom-2 {{ innerI18n.t('krea.educationalContent.features.ageRangeLabel') }}
            .width-100.ages-select.m-bottom-1(v-if='!hasAges || ageEdition')
              krea-multiple-k-select(
                name='ages'
                :maxSelected='2'
                keyName='id'
                :value='agesValue'
                :options='agesOptions'
                @selectOption='editContentAges'
                small noActions
                :placeholder='innerI18n.t("krea.educationalContent.features.agesPlaceholder")'
              )
            .width-100
              p.row.middle.text-normal.age-desc(
                v-if='content.ageRangeId'
                :style="getAgeRangeStyle(content.ageRangeId)"
              ) {{ innerI18n.t('listPrograms.ageRange', getAge(content.ageRangeId)) }}
              p.row.middle.text-normal.age-desc(
                v-if='content.secondaryAgeRangeId'
                :style='getAgeRangeStyle(content.secondaryAgeRangeId)'
              ) {{ innerI18n.t('listPrograms.ageRange', getAge(content.secondaryAgeRangeId)) }}
        template(slot='popover')
          actions(
            @edit='toggleAgeEdition'
            placement='start'
            :info='agesInfo'
            edit
          )
</template>

<script>
import Actions from '@/components/Krea/Inputs/Actions/Actions'
import bookIcon from '@/assets/ic_book.svg'
import userIcon from '@/assets/ic_user.svg'
import calendarIcon from '@/assets/ic_calendar.svg'
import KreaMultipleKSelect from '@/components/Krea/Inputs/KreaMultipleKSelect'
import { mapActions, mapState } from 'vuex'
import userRole from '@/mixins/userRole'
import { innerI18n } from '../../../main'
import { contentType } from '../../../utils/constants'

export default {
  components: { KreaMultipleKSelect, Actions },
  mixins: [userRole],
  data() {
    return {
      bookIcon,
      userIcon,
      calendarIcon,
      innerI18n,
      ageEdition: false
    }
  },
  computed: {
    ...mapState({
      ages: state => state.dashboard.ages,
      content: state => state.kreaEducationalContent.content,
      innerLocale: state => state.behavior.innerLocale
    }),
    kreaContentGuide() {
      return process.env[`VUE_APP_KREA_CREATE_CONTENT_GUIDE_${this.innerLocale}`]
    },
    agesValue() {
      return [this.content.ageRangeId, this.content.secondaryAgeRangeId].filter(age => age)
    },
    agesOptions() {
      return this.ages
        .filter(age => this.userEducationalLevels.includes(age.educationalLevel))
        .map(age => ({
          ...age,
          label: `\
          <div class='age-option' style='--color: ${age.mainColor}'>\
            ${this.innerI18n.t('resourceCard.agesTag', { min: age.minAge, max: age.maxAge })}\
          </div>`
        }))
    },
    agesInfo() {
      return {
        title: innerI18n.t('krea.educationalContent.features.agesInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.educationalContent.features.agesInfo', { content: this.contentTypeTranslation })]
      }
    },
    hasAges() {
      return this.content.ageRangeId || this.content.secondaryAgeRangeId
    },
    contentTypeTranslation() {
      const contentTypeName = Object.keys(contentType).find(key => this.content.contentType === contentType[key])
      return innerI18n.t(`krea.educationalContent.${contentTypeName}`).toLowerCase()
    }
  },
  methods: {
    ...mapActions([
      'editKreaContentField'
    ]),
    getAgeRangeStyle(ageId) {
      return {
        '--age-color': this.getAge(ageId).mainColor
      }
    },
    getAge(ageId) {
      return this.ages.find(age => age.id === ageId)
    },
    toggleAgeEdition() {
      this.ageEdition = !this.ageEdition
    },
    editContentAges(name, ages) {
      if (ages.length === 2) {
        this.editKreaContentField({ field: 'ageRangeId', value: ages[0] })
        this.editKreaContentField({ field: 'secondaryAgeRangeId', value: ages[1] })
      } else {
        this.editKreaContentField({ field: 'ageRangeId', value: ages[0] })
        this.editKreaContentField({ field: 'secondaryAgeRangeId', value: null })
      }
      this.toggleAgeEdition()
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.ages-select{
  max-width: 170px;
}

.feature {
  width: 162px;
}

.feature-icon {
  width: 20px;
  height: 20px;
}

.feature-title {
  color: $dark-gray;
}

.age-desc {
  position: relative;
  padding-left: 20px;
  margin-bottom: 5px;

  &::before {
    content: '';
    background-color: var(--age-color);
    border-radius: 100%;
    height: 8px;
    position: absolute;
    margin-right: 10px;
    width: 8px;
    left: 0px;
  }
}
</style>
